import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import queryClient from '../../../app/utils/queryClient';
import IUserCompany from '../../user/interfaces/IUserCompany';
import { useOidc, useOidcAccessToken } from '@axa-fr/react-oidc';
import { useMatomo } from '@jonkoops/matomo-tracker-react';

type IAuthContext = {
	authContext: string | null;
	setAuthContext: (context: 'employee' | 'investor' | null) => void;
	authAccessToken: string | null;
	setAuthAccessToken: (accessToken: string) => void;
	authOverrideUserId: number | null;
	setAuthOverrideUserId: (userId: number) => void;
	resetAuthOverrideUserId: () => void;
	resetAuthAccessToken: () => void;
	currentCompany: IUserCompany | null;
	setCurrentCompany: (investorCompany: IUserCompany | null) => void;
	resetCurrentCompany: () => void;

	authUserProfile: { email?: string; name?: string } | null;
	setAuthUserProfile: (userProfile: object) => void;

	authProvider: string | null;
	setAuthProvider: (authProvider: string) => void;
	resetAuthProvider: () => void;

	isInvestor: boolean;
	isAuthenticated: boolean;

	redirectUri: string | null;
	setRedirectUri: (redirectUri: string) => void;
	resetRedirectUri: () => void;

	logout: () => void;
};

const noop = () => {
	null;
};

const AuthContext = createContext<IAuthContext>({
	authContext: null,
	setAuthContext: noop,
	authAccessToken: null,
	setAuthAccessToken: noop,
	isInvestor: false,
	isAuthenticated: false,
	redirectUri: null,
	setRedirectUri: noop,
	resetRedirectUri: noop,
	resetAuthOverrideUserId: noop,
	resetAuthAccessToken: noop,
	logout: noop,
	authOverrideUserId: null,
	setAuthOverrideUserId: noop,
	currentCompany: null,
	setCurrentCompany: noop,
	resetCurrentCompany: noop,

	authUserProfile: null,
	setAuthUserProfile: noop,
	authProvider: null,
	setAuthProvider: noop,
	resetAuthProvider: noop,
});

export const useAuthContext = () => {
	return useContext(AuthContext);
};

type AuthProviderProps = {
	children?: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	// authContext
	const [authContext, changeAuthContext] = useState(
		localStorage.getItem('authContext') ? localStorage.getItem('authContext') : null,
	);
	const setAuthContext = (context: 'employee' | 'investor' | null) => {
		if (context !== null) {
			localStorage.setItem('authContext', context);
		}
		changeAuthContext(context);
	};
	const resetAuthContext = () => {
		changeAuthContext(null);
		localStorage.removeItem('authContext');
	};

	// authAccessToken
	const [authAccessToken, changeAuthAccessToken] = useState(localStorage.getItem('authAccessToken') ?? null);
	const setAuthAccessToken = (accessToken: string) => {
		localStorage.setItem('authAccessToken', accessToken);
		changeAuthAccessToken(accessToken);
	};
	const resetAuthAccessToken = () => {
		changeAuthAccessToken(null);
		localStorage.removeItem('authAccessToken');
	};

	// authOverrideUserId
	const [authOverrideUserId, changeAuthOverrideUserId] = useState<number | null>(
		localStorage.getItem('authOverrideUserId') === null
			? null
			: Number.parseInt(localStorage.getItem('authOverrideUserId') as string),
	);
	const setAuthOverrideUserId = (authOverrideUserId: number) => {
		if (authOverrideUserId !== null) {
			localStorage.setItem('authOverrideUserId', authOverrideUserId.toString());
			queryClient.removeQueries();
			changeAuthOverrideUserId(authOverrideUserId);
		}
	};
	const resetAuthOverrideUserId = () => {
		changeAuthOverrideUserId(null);
		localStorage.removeItem('authOverrideUserId');
	};

	// currentCompany
	const [currentCompany, changeCurrentCompany] = useState<IUserCompany | null>(
		localStorage.getItem('authCurrentCompany') === null ||
			localStorage.getItem('authCurrentCompany') === 'undefined'
			? null
			: JSON.parse(localStorage.getItem('authCurrentCompany') as string),
	);
	const setCurrentCompany = (currentCompany: IUserCompany) => {
		if (currentCompany !== null && currentCompany !== undefined && currentCompany.id) {
			localStorage.setItem('authCurrentCompany', JSON.stringify(currentCompany));
			queryClient.removeQueries();
			changeCurrentCompany(currentCompany);
		}
	};
	const resetCurrentCompany = () => {
		changeCurrentCompany(null);
		queryClient.removeQueries();
		localStorage.removeItem('authCurrentCompany');
	};

	// authUserProfile
	const [authUserProfile, changeAuthUserProfile] = useState(
		localStorage.getItem('userProfile') ? JSON.parse(localStorage.getItem('userProfile') as string) : null,
	);
	const setAuthUserProfile = useCallback((userProfile: object) => {
		localStorage.setItem('userProfile', JSON.stringify(userProfile));
		changeAuthUserProfile(userProfile);
	}, []);
	const resetAuthUserProfile = () => {
		changeAuthUserProfile(null);
		localStorage.removeItem('userProfile');
	};

	// redirectUri
	const [redirectUri, changeRedirectUri] = useState(localStorage.getItem('authRedirectUri') ?? null);
	const setRedirectUri = (redirectUri: string) => {
		localStorage.setItem('authRedirectUri', redirectUri);
		changeRedirectUri(redirectUri);
	};
	const resetRedirectUri = () => {
		changeRedirectUri(null);
		localStorage.removeItem('authRedirectUri');
	};

	// authProvider
	const [authProvider, changeAuthProvider] = useState(localStorage.getItem('authProvider') ?? null);
	const setAuthProvider = (authProvider: string) => {
		localStorage.setItem('authProvider', authProvider);
		changeAuthProvider(authProvider);
	};
	const resetAuthProvider = () => {
		changeAuthProvider(null);
		localStorage.removeItem('authProvider');
	};

	const { pushInstruction } = useMatomo();
	const logout = useCallback(() => {
		// DONT Remove redirect uri (resetRedirectUri) here only on logout and in token expired method in Oktas Hooks
		resetAuthContext();
		resetAuthAccessToken();
		resetAuthOverrideUserId();
		resetAuthUserProfile();
		resetCurrentCompany();
		// ref: https://developer.matomo.org/guides/tracking-javascript-guide#retrieving-a-custom-dimension-value
		pushInstruction('resetUserId');
		pushInstruction('appendToTrackingUrl', 'new_visit=1');
		pushInstruction('trackPageView');
		pushInstruction('appendToTrackingUrl', '');

		queryClient.removeQueries();
	}, [pushInstruction]);

	const oidc = useOidc();
	const { accessToken } = useOidcAccessToken();
	const contextValue = React.useMemo(
		() => ({
			authContext,
			setAuthContext,
			authAccessToken: authAccessToken ?? accessToken,
			setAuthAccessToken,
			logout,
			authOverrideUserId,
			setAuthOverrideUserId,
			resetAuthOverrideUserId,
			currentCompany,
			setCurrentCompany,
			resetCurrentCompany,

			authUserProfile,
			setAuthUserProfile,
			redirectUri,
			setRedirectUri,
			resetRedirectUri,
			isAuthenticated: authAccessToken !== null || oidc.isAuthenticated,
			isInvestor:
				(authAccessToken !== null || oidc.isAuthenticated) &&
				(authContext === 'investor' || (authOverrideUserId !== null && authOverrideUserId >= 0)),
			authProvider,
			setAuthProvider,
			resetAuthProvider,
			resetAuthAccessToken,
		}),
		[
			authContext,
			authAccessToken,
			accessToken,
			logout,
			authOverrideUserId,
			currentCompany,
			authUserProfile,
			setAuthUserProfile,
			redirectUri,
			oidc.isAuthenticated,
			authProvider,
		],
	);

	return <AuthContext.Provider value={contextValue as IAuthContext}>{children}</AuthContext.Provider>;
};
